import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { devicePayload } from "./device";
import analytics from "./analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("rcsa_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("rcsa_token");
    if (token) {
      api
        .get("/auth/me")
        .then((r) => {
          setUser(r.data);
          localStorage.setItem("rcsa_user", JSON.stringify(r.data));
        })
        .catch(() => {
          localStorage.removeItem("rcsa_token");
          localStorage.removeItem("rcsa_user");
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password, device: devicePayload() });
    if (data.otp_required) {
      // New device — caller must collect the emailed OTP. Nothing stored yet.
      return data;
    }
    localStorage.setItem("rcsa_token", data.token);
    localStorage.setItem("rcsa_user", JSON.stringify(data.user));
    setUser(data.user);
    analytics.login("email/password");
    return data.user;
  };

  const verifyDeviceOtp = async (email, password, code) => {
    const { data } = await api.post("/auth/verify-device-otp", {
      email, password, code, device: devicePayload(),
    });
    localStorage.setItem("rcsa_token", data.token);
    localStorage.setItem("rcsa_user", JSON.stringify(data.user));
    setUser(data.user);
    analytics.login("OTP");
    return data.user;
  };

  const resendDeviceOtp = async (email, password) => {
    const { data } = await api.post("/auth/resend-device-otp", {
      email, password, device: devicePayload(),
    });
    return data;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", { ...payload, device: devicePayload() });
    localStorage.setItem("rcsa_token", data.token);
    localStorage.setItem("rcsa_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const adoptSession = (token, userDoc) => {
    localStorage.setItem("rcsa_token", token);
    localStorage.setItem("rcsa_user", JSON.stringify(userDoc));
    setUser(userDoc);
  };

  const logout = () => {
    localStorage.removeItem("rcsa_token");
    localStorage.removeItem("rcsa_user");
    setUser(null);
  };

  const refresh = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    localStorage.setItem("rcsa_user", JSON.stringify(data));
    return data;
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, signup, logout, refresh, adoptSession, verifyDeviceOtp, resendDeviceOtp }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
